import { CalendarDate } from '@internationalized/date';
import {
  Money,
  PaymentMethod,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import { atom, Getter } from 'jotai';

import { billMarkAsPaidAtom } from '../_graphql/mutationBillMarkAsPaid';
import { billUpdateAtom } from '../_graphql/mutationBillUpdate';
import { billVoidAtom } from '../_graphql/mutationBillVoid';
import { billDetailQueryAtom } from '../_graphql/queryBillDetail';
import { financeRoutes } from '../routes';
import { BillDetailsTab, BillLineItemRecord, BillRecord } from '../types';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

export type DraftBillInfo = {
  documentDate: CalendarDate | null;
  documentNumber: string | null;
  dueDate: CalendarDate | null;
  eligibleAt: CalendarDate | null;
  paymentTerm: PaymentTerm | null;
  preferredPaymentMethod: PaymentMethod | null;
  notes: string | null;
  amount: Money | null;
  facilityId: string | null;
};

// 🟣🟣🟣 PARAMS - Bill Detail View
export const $billDetailPathParams = atomFromRoutePath(
  financeRoutes.billDetail,
);
export const $billId = atom((get) =>
  get($billDetailPathParams)?.id
    ? String(get($billDetailPathParams)?.id)
    : null,
);

// 🟢🟢🟢 REMOTE DATA - Bill Detail
export const $remoteBillDetail = billDetailQueryAtom((get) => ({
  variables: { id: get($billId) ?? '' },
  skip: !get($billId),
}));

const refetchBillDetail = (get: Getter) => {
  const billId = get($billId);
  if (!billId) {
    return;
  }
  get($remoteBillDetail).refetch?.();
};

// 🟠🟠🟠 ACTIONS - Bill Update
export const $updateBill = billUpdateAtom({
  onSuccess: (get, set) => {
    set($currentDraftLineItems, null);
    set($currentDraftBillInfo, null);
    refetchBillDetail(get);
    set($syncDraftWithRemote, true);
  },
});

// 🟠🟠🟠 ACTIONS - Bill Void
export const $voidBill = billVoidAtom({
  onSuccess: (get) => {
    refetchBillDetail(get);
  },
});

// 🟠🟠🟠 ACTIONS - Bill Mark As Paid
export const $markBillAsPaid = billMarkAsPaidAtom({
  onSuccess: (get) => {
    refetchBillDetail(get);
  },
});

// 🔘🔘🔘 PLAIN ATOMS - UI State
export const $tab = atomFromQueryString.string<BillDetailsTab>('tab');
export const $isEditMode = atom<boolean>(false);
export const $hasUnsavedChanges = atom<boolean>(false);

export const $isBillLoading = atom((get) => {
  const data = get($remoteBillDetail);
  return data?.isLoading ?? false;
});

export const $billError = atom((get) => {
  const data = get($remoteBillDetail);
  return data?.error ?? null;
});

// 🟡🟡🟡 DRAFT STATE - Line Items Draft Changes
export const $draftLineItems = atom<Map<string, BillLineItemRecord[]>>(
  new Map(),
);

export const $currentDraftLineItems = atom(
  (get) => {
    const billId = get($billId);
    const draftMap = get($draftLineItems);
    return billId ? (draftMap.get(billId) ?? null) : null;
  },
  (get, set, lineItems: BillLineItemRecord[] | null) => {
    const billId = get($billId);
    if (!billId) {
      return;
    }

    set($draftLineItems, (prev) => {
      const newMap = new Map(prev);
      if (lineItems === null) {
        newMap.delete(billId);
      } else {
        newMap.set(billId, lineItems);
      }
      return newMap;
    });
  },
);

export const $effectiveLineItems = atom((get) => {
  const result = get($remoteBillDetail);
  const draftLineItems = get($currentDraftLineItems);
  const remoteLineItems = result?.data?.bill?.lineItems ?? [];

  if (draftLineItems !== null) {
    return draftLineItems;
  }

  return remoteLineItems;
});

export const $lastSavedLineItemsTimestamp = atom<string | null>(null);

export const $syncDraftWithRemote = atom(
  (get) => {
    const result = get($remoteBillDetail);
    const lastTimestamp = get($lastSavedLineItemsTimestamp);

    if (result?.data?.bill?.timestamps?.updatedAt) {
      return result.data.bill.timestamps.updatedAt.toString() !== lastTimestamp;
    }
    return false;
  },
  (get, set, shouldClearDraft = false) => {
    const result = get($remoteBillDetail);
    const currentDraft = get($currentDraftLineItems);

    if (result?.data?.bill?.timestamps?.updatedAt) {
      const currentTimestamp = result.data.bill.timestamps.updatedAt.toString();
      set($lastSavedLineItemsTimestamp, currentTimestamp);

      if (shouldClearDraft && currentDraft !== null) {
        set($currentDraftLineItems, null);
      }
    }
  },
);

export const $hasDraftChanges = atom((get) => {
  const result = get($remoteBillDetail);
  const draftLineItems = get($currentDraftLineItems);

  if (!result?.data?.bill || draftLineItems === null) {
    return false;
  }

  const originalLineItems = result.data.bill.lineItems ?? [];

  if (originalLineItems.length !== draftLineItems.length) {
    return true;
  }

  return draftLineItems.some((draftItem, index) => {
    const originalItem = originalLineItems[index];
    if (!originalItem) return true;

    return (
      draftItem.description !== originalItem.description ||
      draftItem.quantity !== originalItem.quantity ||
      draftItem.unit !== originalItem.unit ||
      draftItem.unitPrice?.amount !== originalItem.unitPrice?.amount ||
      draftItem.unitPrice?.currencyCode !== originalItem.unitPrice?.currencyCode
    );
  });
});

export const $clearCurrentDraftLineItems = atom(null, (get, set) => {
  set($currentDraftLineItems, null);
});

export const $draftBillInfo = atom<Map<string, DraftBillInfo>>(new Map());

export const $currentDraftBillInfo = atom(
  (get) => {
    const billId = get($billId);
    const draftMap = get($draftBillInfo);
    return billId ? (draftMap.get(billId) ?? null) : null;
  },
  (get, set, info: DraftBillInfo | null) => {
    const billId = get($billId);
    if (!billId) {
      return;
    }

    set($draftBillInfo, (prev) => {
      const newMap = new Map(prev);
      if (info === null) {
        newMap.delete(billId);
      } else {
        newMap.set(billId, info);
      }
      return newMap;
    });
  },
);

export const $effectiveBillInfo = atom((get) => {
  const result = get($remoteBillDetail);
  const draftInfo = get($currentDraftBillInfo);
  const remoteBill = result?.data?.bill;

  if (!remoteBill) {
    return null;
  }

  if (draftInfo !== null) {
    return {
      ...remoteBill,
      documentDate: draftInfo.documentDate,
      documentNumber: draftInfo.documentNumber,
      dueDate: draftInfo.dueDate,
      eligibleAt: draftInfo.eligibleAt,
      paymentTerm: draftInfo.paymentTerm,
      preferredPaymentMethod: draftInfo.preferredPaymentMethod,
      notes: draftInfo.notes,
      amount: draftInfo.amount ?? remoteBill.amount,
      facilityId: draftInfo.facilityId ?? remoteBill.facilityId,
    };
  }

  return remoteBill;
});

export const $hasDraftInfoChanges = atom((get) => {
  const result = get($remoteBillDetail);
  const draftInfo = get($currentDraftBillInfo);

  if (!result?.data?.bill || draftInfo === null) {
    return false;
  }

  const bill = result.data.bill;

  const documentDateChanged =
    (draftInfo.documentDate?.toString() ?? null) !==
    (bill.documentDate?.toString() ?? null);
  const documentNumberChanged =
    draftInfo.documentNumber !== bill.documentNumber;
  const dueDateChanged =
    (draftInfo.dueDate?.toString() ?? null) !==
    (bill.dueDate?.toString() ?? null);
  const eligibleAtChanged =
    (draftInfo.eligibleAt?.toString() ?? null) !==
    (bill.eligibleAt?.toString() ?? null);
  const paymentTermChanged = draftInfo.paymentTerm !== bill.paymentTerm;
  const paymentMethodChanged =
    draftInfo.preferredPaymentMethod !== bill.preferredPaymentMethod;
  const notesChanged = draftInfo.notes !== bill.notes;
  const amountChanged =
    draftInfo.amount?.amount !== bill.amount?.amount ||
    draftInfo.amount?.currencyCode !== bill.amount?.currencyCode;
  const facilityIdChanged = draftInfo.facilityId !== bill.facilityId;

  return (
    documentDateChanged ||
    documentNumberChanged ||
    dueDateChanged ||
    eligibleAtChanged ||
    paymentTermChanged ||
    paymentMethodChanged ||
    notesChanged ||
    amountChanged ||
    facilityIdChanged
  );
});

export const $isDirty = atom((get) => {
  const hasDraftLineItemChanges = get($hasDraftChanges);
  const hasDraftInfoChanges = get($hasDraftInfoChanges);
  return hasDraftLineItemChanges || hasDraftInfoChanges;
});

export const $discardAllDrafts = atom(null, (get, set) => {
  set($currentDraftLineItems, null);
  set($currentDraftBillInfo, null);
});

export const $initDraftFromBill = atom(null, (get, set, bill: BillRecord) => {
  set($currentDraftBillInfo, {
    documentDate: bill.documentDate,
    documentNumber: bill.documentNumber,
    dueDate: bill.dueDate,
    eligibleAt: bill.eligibleAt,
    paymentTerm: bill.paymentTerm,
    preferredPaymentMethod: bill.preferredPaymentMethod,
    notes: bill.notes,
    amount: bill.amount,
    facilityId: bill.facilityId,
  });
});
